import type { RefObject } from "react";
import { useLayoutEffect, useRef } from "react";
import type { OnboardingStep } from "./useOnboardingState";

function applyDimensions(card: HTMLDivElement, height: string, maxWidth: string) {
  card.style.height = height;
  card.style.maxWidth = maxWidth;
}

function resetStyles(card: HTMLDivElement) {
  card.style.transition = "";
  card.style.height = "";
  card.style.maxWidth = "";
  card.style.overflow = "";
}

/**
 * useMorphCard — animates a card element between height + max-width states
 * when the active step changes. Mirrors the demo's cxObMorphCard().
 * Returns a ref to attach to the card's container element.
 */
export function useMorphCard(step: OnboardingStep): RefObject<HTMLDivElement> {
  const cardRef = useRef<HTMLDivElement>(null);
  const prevDimensions = useRef<{ height: string; maxWidth: string } | null>(null);

  // We MUST use useLayoutEffect instead of useEffect here.
  // We need to synchronously measure the DOM immediately after React commits the new step,
  // before the browser paints the screen. If we used useEffect, there would be a visual flicker
  // where the new step renders at its natural size before the animation kicks in.
  //
  // biome-ignore lint/correctness/useExhaustiveDependencies: We intentionally only want this effect to run when the `step` changes. Adding other internal dependencies would trigger morph animations erroneously on standard re-renders.
  useLayoutEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    // We read this once per step transition, which is intentional.
    // It's highly unlikely a user toggles system motion preferences exactly while clicking "Continue".
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // To measure the final layout correctly, we MUST disable the CSS transition first.
    // Otherwise, max-width is transitioning and offsetHeight will report a taller height due to text wrapping.
    card.style.transition = "none";
    card.style.height = "";
    card.style.maxWidth = "";

    // Measure the new (current) dimensions AFTER the DOM has updated for the new step
    const toHeight = `${card.offsetHeight}px`;
    const toMaxWidth = getComputedStyle(card).maxWidth;

    // If reduced motion is preferred or it's the first render, just store dimensions and skip animation
    if (prefersReducedMotion || !prevDimensions.current) {
      card.style.transition = "";
      prevDimensions.current = { height: toHeight, maxWidth: toMaxWidth };
      return;
    }

    const from = prevDimensions.current;

    // Store new dimensions for the next step change
    prevDimensions.current = { height: toHeight, maxWidth: toMaxWidth };

    // Skip morphing if dimensions haven't changed
    if (from.height === toHeight && from.maxWidth === toMaxWidth) {
      card.style.transition = "";
      return;
    }

    // Set transition to none and apply previous dimensions
    card.style.transition = "none";
    applyDimensions(card, from.height, from.maxWidth);
    card.style.overflow = "hidden";

    // Force reflow to apply the old dimensions without transition
    void card.offsetWidth;

    // Remove transition inline style to let CSS transitions take over
    card.style.transition = "";
    applyDimensions(card, toHeight, toMaxWidth);

    let fallbackTimeout: ReturnType<typeof setTimeout>;

    const cleanup = () => {
      resetStyles(card);
      card.removeEventListener("transitionend", handleTransitionEnd);
      clearTimeout(fallbackTimeout);
    };

    const handleTransitionEnd = (e: TransitionEvent) => {
      if (e.target === card && (e.propertyName === "height" || e.propertyName === "max-width")) {
        cleanup();
      }
    };

    card.addEventListener("transitionend", handleTransitionEnd);

    // Fallback in case transitionend doesn't fire
    fallbackTimeout = setTimeout(cleanup, 1500);

    return cleanup;
  }, [step]);

  return cardRef;
}
